import { useState, useCallback, useRef, useEffect } from 'react';
import { Layer, UseLayersReturn, ImageLayerData, TextLayerData } from '../types';
import { createTextCanvas, getDefaultUvSize, loadImageFromFile } from '../utils/textureUtils';

type HistorySnapshot = {
  layers: Layer[];
  selectedLayerId: string | null;
  materialColors: Record<string, string>;
};

type UseLayersOptions = {
  materialColorsRef?: { current: Record<string, string> };
  onRestoreMaterialColors?: (colors: Record<string, string>) => void;
};

/**
 * Hook for managing t-shirt layers
 */
export function useLayers(options?: UseLayersOptions): UseLayersReturn {
  const [layers, setLayers] = useState<Layer[]>([]);
  const [selectedLayerId, setSelectedLayerId] = useState<string | null>(null);
  const selectedLayerIdRef = useRef<string | null>(null);
  const historyRef = useRef<HistorySnapshot[]>([]);
  const historyIndexRef = useRef(-1);
  const optionsRef = useRef(options);
  optionsRef.current = options;
  // Set to true during undo/redo so callers can skip stale layer→color syncs
  const historyRestorationRef = useRef(false);
  const isRestoringRef = useRef(false);
  const [historyState, setHistoryState] = useState({ index: -1, length: 0 });
  const DEFAULT_UV_CENTER: [number, number] = [0.235, 0.7];

  /**
   * Selected layer
   */
  const selectedLayer = layers.find(layer => layer.id === selectedLayerId) || null;

  const cloneLayerData = (data?: Layer['data']) => {
    if (!data || typeof data !== 'object') return data;
    const next: any = { ...data };
    if ((data as any).originalSize) {
      next.originalSize = { ...(data as any).originalSize };
    }
    if ((data as any).selectedMaterialIds) {
      next.selectedMaterialIds = [...(data as any).selectedMaterialIds];
    }
    if ((data as any).motifMaterialColors) {
      next.motifMaterialColors = { ...(data as any).motifMaterialColors };
    }
    if ((data as any).motifDesignColors) {
      next.motifDesignColors = { ...(data as any).motifDesignColors };
    }
    if ((data as any).materialColorsSnapshot) {
      next.materialColorsSnapshot = { ...(data as any).materialColorsSnapshot };
    }
    if ((data as any).motifPatternColors) {
      next.motifPatternColors = { ...(data as any).motifPatternColors };
    }
    return next;
  };

  const cloneLayers = (nextLayers: Layer[]) =>
    nextLayers.map(layer => ({
      ...layer,
      uvCenter: [...layer.uvCenter] as [number, number],
      uvSize: [...layer.uvSize] as [number, number],
      data: cloneLayerData(layer.data),
    }));

  const getLayerHash = (layer: Layer) => {
    const data = layer.data as any;
    const parts = [
      layer.id,
      layer.type,
      layer.zone,
      layer.locked ? 1 : 0,
      layer.order,
      layer.transformMode,
      layer.uvCenter.join(','),
      layer.uvSize.join(','),
      layer.uvRotation,
      data?.fileName || '',
      data?.text || '',
      data?.fontFamily || '',
      data?.fontSize || '',
      data?.color || '',
      data?.strokeColor || '',
      data?.strokeWidth || '',
      data?.canvasResolution || '',
      data?.kind || '',
      data?.isMotif ? 1 : 0,
      data?.isPatternMotif ? 1 : 0,
      data?.patternMaterialId || '',
      data?.patternDesignKey || '',
      (data?.selectedMaterialIds || []).join('|'),
      Object.entries(data?.motifMaterialColors || {}).sort().map(([k, v]) => `${k}:${v}`).join('|'),
      Object.entries(data?.motifPatternColors || {}).sort().map(([k, v]) => `${k}:${v}`).join('|'),
      Object.entries(data?.motifDesignColors || {}).sort().map(([k, v]) => `${k}:${v}`).join('|'),
      Object.entries(data?.materialColorsSnapshot || {}).sort().map(([k, v]) => `${k}:${v}`).join('|'),
      data?.originalSize ? `${data.originalSize.width}x${data.originalSize.height}` : '',
    ];
    return parts.join('::');
  };

  const areArraysEqual = (a?: any[], b?: any[]) => {
    if (a === b) return true;
    if (!a || !b || a.length !== b.length) return false;
    for (let i = 0; i < a.length; i += 1) {
      if (a[i] !== b[i]) return false;
    }
    return true;
  };

  const areRecordsEqual = (a?: Record<string, any>, b?: Record<string, any>) => {
    if (a === b) return true;
    if (!a || !b) return false;
    const aKeys = Object.keys(a);
    const bKeys = Object.keys(b);
    if (aKeys.length !== bKeys.length) return false;
    for (const key of aKeys) {
      if (a[key] !== b[key]) return false;
    }
    return true;
  };

  const areLayerDataEqual = (a?: Layer['data'], b?: Layer['data']) => {
    if (a === b) return true;
    if (!a || !b) return false;
    const aAny = a as any;
    const bAny = b as any;
    if (aAny.fileName !== bAny.fileName) return false;
    if ((aAny.kind || '') !== (bAny.kind || '')) return false;
    if ((aAny.text || '') !== (bAny.text || '')) return false;
    if ((aAny.fontFamily || '') !== (bAny.fontFamily || '')) return false;
    if ((aAny.fontSize || 0) !== (bAny.fontSize || 0)) return false;
    if ((aAny.color || '') !== (bAny.color || '')) return false;
    if ((aAny.strokeColor || '') !== (bAny.strokeColor || '')) return false;
    if ((aAny.strokeWidth || 0) !== (bAny.strokeWidth || 0)) return false;
    if ((aAny.canvasResolution || 0) !== (bAny.canvasResolution || 0)) return false;
    if ((aAny.isMotif || false) !== (bAny.isMotif || false)) return false;
    if ((aAny.isPatternMotif || false) !== (bAny.isPatternMotif || false)) return false;
    if ((aAny.patternMaterialId || '') !== (bAny.patternMaterialId || '')) return false;
    if ((aAny.patternDesignKey || '') !== (bAny.patternDesignKey || '')) return false;
    if (!areArraysEqual(aAny.selectedMaterialIds, bAny.selectedMaterialIds)) return false;
    if (!areRecordsEqual(aAny.motifMaterialColors, bAny.motifMaterialColors)) return false;
    if (!areRecordsEqual(aAny.motifPatternColors, bAny.motifPatternColors)) return false;
    if (!areRecordsEqual(aAny.motifDesignColors, bAny.motifDesignColors)) return false;
    if (aAny.originalSize || bAny.originalSize) {
      if (!aAny.originalSize || !bAny.originalSize) return false;
      if (aAny.originalSize.width !== bAny.originalSize.width) return false;
      if (aAny.originalSize.height !== bAny.originalSize.height) return false;
    }
    return true;
  };

  const areLayersEqual = (a: Layer[], b: Layer[]) => {
    if (a === b) return true;
    if (a.length !== b.length) return false;
    for (let i = 0; i < a.length; i += 1) {
      if (getLayerHash(a[i]) !== getLayerHash(b[i])) return false;
    }
    return true;
  };

  const pushHistory = (nextLayers: Layer[], nextSelected: string | null, materialColors?: Record<string, string>) => {
    if (isRestoringRef.current) return;
    const colors = materialColors ?? optionsRef.current?.materialColorsRef?.current ?? {};
    const snapshot: HistorySnapshot = { layers: cloneLayers(nextLayers), selectedLayerId: nextSelected, materialColors: { ...colors } };
    const history = historyRef.current;
    const last = history[historyIndexRef.current];
    if (last && last.selectedLayerId === snapshot.selectedLayerId && areLayersEqual(last.layers, snapshot.layers)) {
      return;
    }
    const nextHistory = history.slice(0, historyIndexRef.current + 1);
    nextHistory.push(snapshot);
    if (nextHistory.length > 60) {
      nextHistory.shift();
    }
    historyRef.current = nextHistory;
    historyIndexRef.current = nextHistory.length - 1;
    setHistoryState({ index: historyIndexRef.current, length: nextHistory.length });
  };

  const pushColorHistory = useCallback((materialColors: Record<string, string>) => {
    if (isRestoringRef.current) return;
    const snapshot: HistorySnapshot = {
      layers: cloneLayers(historyRef.current[historyIndexRef.current]?.layers ?? []),
      selectedLayerId: selectedLayerIdRef.current,
      materialColors: { ...materialColors },
    };
    const history = historyRef.current;
    const last = history[historyIndexRef.current];
    const lastColors = last?.materialColors ?? {};
    const colorKeys = Object.keys(materialColors);
    const lastKeys = Object.keys(lastColors);
    if (
      last &&
      areLayersEqual(last.layers, snapshot.layers) &&
      colorKeys.length === lastKeys.length &&
      colorKeys.every(k => lastColors[k] === materialColors[k])
    ) {
      return;
    }
    const nextHistory = history.slice(0, historyIndexRef.current + 1);
    nextHistory.push(snapshot);
    if (nextHistory.length > 60) nextHistory.shift();
    historyRef.current = nextHistory;
    historyIndexRef.current = nextHistory.length - 1;
    setHistoryState({ index: historyIndexRef.current, length: nextHistory.length });
  }, []);

  const setSelectedLayerIdSafe = (nextId: string | null) => {
    selectedLayerIdRef.current = nextId;
    setSelectedLayerId(nextId);
  };

  useEffect(() => {
    if (historyRef.current.length === 0) {
      pushHistory(layers, selectedLayerIdRef.current);
    }
  }, []);

  /**
   * Generates a unique ID for a new layer
   */
  const generateLayerId = useCallback((): string => {
    return `layer_${Date.now()}_${Math.random().toString(36).substr(2, 9)}`;
  }, []);

  /**
   * Gets the current max order
   */
  const getMaxOrder = useCallback((): number => {
    if (layers.length === 0) return 0;
    return Math.max(...layers.map(layer => layer.order));
  }, [layers]);

  const getDefaultUvCenter = useCallback((uvSize: [number, number]) => {
    const halfU = uvSize[0] / 2;
    const halfV = uvSize[1] / 2;
    const clampU = (value: number) => Math.min(1 - halfU, Math.max(halfU, value));
    const clampV = (value: number) => Math.min(1 - halfV, Math.max(halfV, value));
    const u = clampU(DEFAULT_UV_CENTER[0]);
    const v = clampV(DEFAULT_UV_CENTER[1]);
    return [u, v] as [number, number];
  }, [DEFAULT_UV_CENTER]);

  /**
   * Adds a new layer
   */
  const addLayer = useCallback((layerData: Omit<Layer, 'id' | 'order'>) => {
    const newLayer: Layer = {
      ...layerData,
      id: generateLayerId(),
      order: getMaxOrder() + 1,
    };

    setLayers(prevLayers => {
      const nextLayers = [...prevLayers, newLayer];
      pushHistory(nextLayers, selectedLayerIdRef.current);
      return nextLayers;
    });
    return newLayer;
  }, [generateLayerId, getMaxOrder]);

  /**
   * Removes a layer
   */
  const removeLayer = useCallback((layerId: string) => {
    setLayers(prevLayers => {
      const nextLayers = prevLayers.filter(layer => layer.id !== layerId);
      const nextSelected = selectedLayerIdRef.current === layerId ? null : selectedLayerIdRef.current;
      if (nextSelected !== selectedLayerIdRef.current) {
        setSelectedLayerIdSafe(nextSelected);
      }
      pushHistory(nextLayers, nextSelected);
      return nextLayers;
    });
  }, []);

  /**
   * Updates a layer
   */
  const updateLayer = useCallback((layerId: string, updates: Partial<Layer>, options?: { silent?: boolean }) => {
    setLayers(prevLayers => {
      const nextLayers = prevLayers.map(layer =>
        layer.id === layerId ? { ...layer, ...updates } : layer
      );
      if (!options?.silent) {
        pushHistory(nextLayers, selectedLayerIdRef.current);
      }
      return nextLayers;
    });
  }, []);

  /**
   * Selects a layer
   */
  const selectLayer = useCallback((layerId: string | null) => {
    setSelectedLayerIdSafe(layerId);
  }, []);

  /**
   * Reorders layers
   */
  const reorderLayers = useCallback((layerId: string, newOrder: number) => {
    setLayers(prevLayers => {
      const updatedLayers = [...prevLayers];
      const layerIndex = updatedLayers.findIndex(layer => layer.id === layerId);

      if (layerIndex === -1) return prevLayers;

      const sorted = [...updatedLayers].sort((a, b) => a.order - b.order);
      const currentIndex = sorted.findIndex(layer => layer.id === layerId);
      if (currentIndex === -1) return prevLayers;

      const targetIndex = newOrder - 1;
      if (targetIndex < 0 || targetIndex >= sorted.length) return prevLayers;

      const [movedLayer] = sorted.splice(currentIndex, 1);
      sorted.splice(targetIndex, 0, movedLayer);

      const normalized = sorted.map((layer, index) => ({
        ...layer,
        order: index + 1,
      }));

      pushHistory(normalized, selectedLayerIdRef.current);
      return normalized;
    });
  }, []);

  /**
   * Adds an image layer
   */
  const addImageLayer = useCallback(async (file: File): Promise<void> => {
    try {
      const image = await loadImageFromFile(file);
      const uvSize = getDefaultUvSize(image.width, image.height, 0.18);

      const imageData: ImageLayerData = {
        fileName: file.name,
        originalSize: {
          width: image.width,
          height: image.height,
        },
        needsInitialPlacement: true,
      };

      addLayer({
        type: 'image',
        zone: 'front',
        locked: false,
        source: image,
        uvCenter: getDefaultUvCenter(uvSize),
        uvSize,
        uvRotation: 0,
        transformMode: 'translate',
        data: imageData,
      });
    } catch (error) {
      throw new Error(`Erreur lors de la creation du layer image: ${error instanceof Error ? error.message : 'Erreur inconnue'}`);
    }
  }, [addLayer]);

  /**
   * Adds a text layer
   */
  const addTextLayer = useCallback((textData: {
    text: string;
    fontFamily: string;
    fontSize: number;
    color: string;
    kind?: 'text' | 'number' | 'name';
    strokeColor?: string;
    strokeWidth?: number;
    canvasResolution?: number;
  }) => {
    try {
      const canvas = createTextCanvas(textData);
      const uvSize = getDefaultUvSize(canvas.width, canvas.height, 0.18);

      const layerTextData: TextLayerData = {
        text: textData.text,
        fontFamily: textData.fontFamily,
        fontSize: textData.fontSize,
        color: textData.color,
        kind: textData.kind,
        strokeColor: textData.strokeColor,
        strokeWidth: textData.strokeWidth,
        canvasResolution: textData.canvasResolution || 1024,
      };

      addLayer({
        type: 'text',
        zone: 'front',
        locked: false,
        source: canvas,
        uvCenter: getDefaultUvCenter(uvSize),
        uvSize,
        uvRotation: 0,
        transformMode: 'translate',
        data: layerTextData,
      });
    } catch (error) {
      throw new Error(`Erreur lors de la creation du layer texte: ${error instanceof Error ? error.message : 'Erreur inconnue'}`);
    }
  }, [addLayer]);

  /**
   * Duplicates a layer
   */
  const duplicateLayer = useCallback((layerId: string) => {
    const originalLayer = layers.find(layer => layer.id === layerId);
    if (!originalLayer) return;

    const DUPLICATE_OFFSET = 0.04;
    const duplicatedLayer: Layer = {
      ...originalLayer,
      id: generateLayerId(),
      order: getMaxOrder() + 1,
      locked: false,
      uvCenter: [
        Math.min(1, originalLayer.uvCenter[0] + DUPLICATE_OFFSET),
        Math.min(1, originalLayer.uvCenter[1] + DUPLICATE_OFFSET),
      ] as [number, number],
      uvSize: [...originalLayer.uvSize] as [number, number],
      uvRotation: originalLayer.uvRotation,
    };

    setLayers(prevLayers => {
      const nextLayers = [...prevLayers, duplicatedLayer];
      pushHistory(nextLayers, selectedLayerIdRef.current);
      return nextLayers;
    });
  }, [layers, generateLayerId, getMaxOrder]);

  /**
   * Clears all layers
   */
  const clearAllLayers = useCallback(() => {
    setLayers(() => {
      setSelectedLayerIdSafe(null);
      pushHistory([], null);
      return [];
    });
  }, []);

  /**
   * Returns layer stats
   */
  const getLayerStats = useCallback(() => {
    return {
      total: layers.length,
      images: layers.filter(layer => layer.type === 'image').length,
      texts: layers.filter(layer => layer.type === 'text').length,
      selected: selectedLayer?.id || null,
    };
  }, [layers, selectedLayer]);

  const undo = useCallback(() => {
    if (historyIndexRef.current <= 0) return;
    const nextIndex = historyIndexRef.current - 1;
    const snapshot = historyRef.current[nextIndex];
    if (!snapshot) return;
    isRestoringRef.current = true;
    historyRestorationRef.current = true;
    historyIndexRef.current = nextIndex;
    setLayers(snapshot.layers);
    setSelectedLayerIdSafe(snapshot.selectedLayerId);
    optionsRef.current?.onRestoreMaterialColors?.(snapshot.materialColors);
    isRestoringRef.current = false;
    setHistoryState({ index: historyIndexRef.current, length: historyRef.current.length });
  }, []);

  const redo = useCallback(() => {
    if (historyIndexRef.current >= historyRef.current.length - 1) return;
    const nextIndex = historyIndexRef.current + 1;
    const snapshot = historyRef.current[nextIndex];
    if (!snapshot) return;
    isRestoringRef.current = true;
    historyRestorationRef.current = true;
    historyIndexRef.current = nextIndex;
    setLayers(snapshot.layers);
    setSelectedLayerIdSafe(snapshot.selectedLayerId);
    optionsRef.current?.onRestoreMaterialColors?.(snapshot.materialColors);
    isRestoringRef.current = false;
    setHistoryState({ index: historyIndexRef.current, length: historyRef.current.length });
  }, []);

  const clearHistory = useCallback(() => {
    const current = historyRef.current[historyIndexRef.current];
    const colors = optionsRef.current?.materialColorsRef?.current ?? {};
    const snapshot: HistorySnapshot = {
      layers: cloneLayers(current?.layers ?? []),
      selectedLayerId: selectedLayerIdRef.current,
      materialColors: { ...colors },
    };
    historyRef.current = [snapshot];
    historyIndexRef.current = 0;
    setHistoryState({ index: 0, length: 1 });
  }, []);

  return {
    layers,
    selectedLayer,
    addLayer,
    removeLayer,
    updateLayer,
    selectLayer,
    reorderLayers,
    getMaxOrder,
    addImageLayer,
    addTextLayer,
    duplicateLayer,
    clearAllLayers,
    getLayerStats,
    undo,
    redo,
    canUndo: historyState.index > 0,
    canRedo: historyState.index < historyState.length - 1,
    pushColorHistory,
    clearHistory,
    historyRestorationRef,
  };
}
